import React, { useState, useEffect, useCallback, useRef } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  CheckCircle2,
  AlertCircle,
  Bell,
  User,
  TrendingUp,
  Palette,
  AlertTriangle,
  Save,
} from "lucide-react";
import { UserAvatar } from "@/components/ui/user-avatar";
import { useApp } from "@/contexts/AppContext";
import { useUserPreferences } from "@/contexts/UserPreferencesContext";
import { useUserPreferenceDefaults } from "@/contexts/UserPreferenceDefaultsContext";
import type { ChartHealthDisplay } from "@/contexts/UserPreferenceDefaultsContext";
import { usePermissions } from "@/hooks/usePermissions";
import { useTerminology } from "@/hooks/useTerminology";
import { DisplayPrecision, ROLE_TEMPLATES } from "@/types/permissions";
import { getDemoRoles } from "@/utils/demoRolePersistence";
import { getRoleTemplateId } from "@/lib/permissions";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useTheme } from "next-themes";
import { updateSelf } from "@/api/services/userService";
import { useToast } from "@/hooks/use-toast";
import { NotificationsTab } from "@/components/settings/NotificationsTab";

export function SettingsPage() {
  const { currentUser, mode } = useApp();
  const { getForecastTerm, getScenarioTerm, getVarianceTerm } = useTerminology();
  const { toast } = useToast();
  const { 
    getUserPrecisionOverride, 
    setUserPrecisionOverride,
    getUserPreferences,
    updateUserPreferences,
  } = useUserPreferences();
  const { defaults } = useUserPreferenceDefaults();
  const { hasAnyPermission } = usePermissions();
  const { setTheme: setAppTheme } = useTheme();
  
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Get current role to check allowUserOverride
  const roleId = getRoleTemplateId(currentUser.forecasterRole);
  const roles = mode === 'demo' ? getDemoRoles() : ROLE_TEMPLATES;
  const currentRole = roles.find(r => r.id === roleId);
  const canOverridePrecision = currentRole?.allowUserOverride || false;

  // Get user preferences
  const userPrefs = getUserPreferences(currentUser.id);
  const userPrecision = getUserPrecisionOverride(currentUser.id);

  // Local state for form values (initialized from user prefs or defaults)
  const [lowCashThreshold, setLowCashThreshold] = useState<string>(() => {
    const value = userPrefs.lowCashBalanceThreshold ?? defaults.lowCashBalanceThreshold;
    return value.toString();
  });
  const [overdueAlert, setOverdueAlert] = useState<boolean>(() => {
    return userPrefs.overdueInvoiceAlert ?? defaults.overdueInvoiceAlert;
  });
  const [varianceThreshold, setVarianceThreshold] = useState<string>(() => {
    const value = userPrefs.varianceAlertThreshold ?? defaults.varianceAlertThreshold;
    return value.toString();
  });
  const [emailNotifications, setEmailNotifications] = useState<boolean>(() => {
    return userPrefs.emailNotifications ?? defaults.emailNotifications;
  });
  const [feedNotifications, setFeedNotifications] = useState<boolean>(() => {
    return userPrefs.feedNotifications ?? defaults.feedNotifications;
  });
  const [defaultView, setDefaultView] = useState<string>(() => {
    return userPrefs.defaultForecastView ?? defaults.defaultForecastView;
  });
  const [confidenceLevel, setConfidenceLevel] = useState<string>(() => {
    return userPrefs.confidenceLevel ?? defaults.confidenceLevel;
  });
  const [includeWeekends, setIncludeWeekends] = useState<boolean>(() => {
    return userPrefs.includeWeekends ?? defaults.includeWeekends;
  });
  const [theme, setTheme] = useState<string>(() => {
    return userPrefs.theme ?? defaults.theme;
  });
  // Chart health zone state
  const [healthyThreshold, setHealthyThreshold] = useState<string>(() => {
    return (userPrefs.healthyThreshold ?? defaults.healthyThreshold).toString();
  });
  const [cautionThreshold, setCautionThreshold] = useState<string>(() => {
    return (userPrefs.cautionThreshold ?? defaults.cautionThreshold).toString();
  });
  const [chartHealthDashboard90, setChartHealthDashboard90] = useState<ChartHealthDisplay>(() => {
    return userPrefs.chartHealthDashboard90 ?? defaults.chartHealthDashboard90;
  });
  const [chartHealthForecast90, setChartHealthForecast90] = useState<ChartHealthDisplay>(() => {
    return userPrefs.chartHealthForecast90 ?? defaults.chartHealthForecast90;
  });
  const [chartHealthForecast12m, setChartHealthForecast12m] = useState<ChartHealthDisplay>(() => {
    return userPrefs.chartHealthForecast12m ?? defaults.chartHealthForecast12m;
  });
  const [chartHealthForecast3y, setChartHealthForecast3y] = useState<ChartHealthDisplay>(() => {
    return userPrefs.chartHealthForecast3y ?? defaults.chartHealthForecast3y;
  });
  // Resolve the current default landing page — same three-layer logic as App.tsx
  const resolveLandingPageDefault = (): string => {
    if (userPrefs.defaultLandingPage) return userPrefs.defaultLandingPage;
    if (defaults.defaultLandingPage) return defaults.defaultLandingPage;
    return 'dashboard';
  };

  const [defaultLandingPage, setDefaultLandingPage] = useState<string>(() => resolveLandingPageDefault());
  const [avatarUrl] = useState<string | null>(() => userPrefs.avatarUrl ?? null);

  // Stable ref so the persona-change effect can call getUserPreferences
  // without adding it to the dependency array.
  const getUserPreferencesRef = React.useRef(getUserPreferences);
  getUserPreferencesRef.current = getUserPreferences;

  // When persona changes: reload form state from context for the new persona.
  // Theme is NOT applied here — ThemeSync in App.tsx handles that exclusively.
  useEffect(() => {
    const prefs = getUserPreferencesRef.current(currentUser.id);
    setLowCashThreshold((prefs.lowCashBalanceThreshold ?? defaults.lowCashBalanceThreshold).toString());
    setOverdueAlert(prefs.overdueInvoiceAlert ?? defaults.overdueInvoiceAlert);
    setVarianceThreshold((prefs.varianceAlertThreshold ?? defaults.varianceAlertThreshold).toString());
    setEmailNotifications(prefs.emailNotifications ?? defaults.emailNotifications);
    setFeedNotifications(prefs.feedNotifications ?? defaults.feedNotifications);
    setDefaultView(prefs.defaultForecastView ?? defaults.defaultForecastView);
    setConfidenceLevel(prefs.confidenceLevel ?? defaults.confidenceLevel);
    setIncludeWeekends(prefs.includeWeekends ?? defaults.includeWeekends);
    setTheme(prefs.theme ?? defaults.theme);
    setDefaultLandingPage(prefs.defaultLandingPage ?? defaults.defaultLandingPage ?? 'dashboard');
    setHealthyThreshold((prefs.healthyThreshold ?? defaults.healthyThreshold).toString());
    setCautionThreshold((prefs.cautionThreshold ?? defaults.cautionThreshold).toString());
    setChartHealthDashboard90(prefs.chartHealthDashboard90 ?? defaults.chartHealthDashboard90);
    setChartHealthForecast90(prefs.chartHealthForecast90 ?? defaults.chartHealthForecast90);
    setChartHealthForecast12m(prefs.chartHealthForecast12m ?? defaults.chartHealthForecast12m);
    setChartHealthForecast3y(prefs.chartHealthForecast3y ?? defaults.chartHealthForecast3y);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser.id]);

  // Handle precision change
  const handlePrecisionChange = (value: DisplayPrecision) => {
    setUserPrecisionOverride(currentUser.id, value);
  };

  // Reset to role default
  const handleResetPrecision = () => {
    setUserPrecisionOverride(currentUser.id, null);
  };

  // Save all preferences
  const handleSave = useCallback(async () => {
    setSaving(true);
    
    const preferencesPayload = {
      lowCashBalanceThreshold: parseFloat(lowCashThreshold) || defaults.lowCashBalanceThreshold,
      overdueInvoiceAlert: overdueAlert,
      varianceAlertThreshold: parseFloat(varianceThreshold) || defaults.varianceAlertThreshold,
      emailNotifications,
      feedNotifications,
      defaultForecastView: defaultView as any,
      confidenceLevel: confidenceLevel as any,
      includeWeekends,
      theme: theme as any,
      defaultLandingPage,
      avatarUrl,
      healthyThreshold: parseFloat(healthyThreshold) || defaults.healthyThreshold,
      cautionThreshold: parseFloat(cautionThreshold) || defaults.cautionThreshold,
      chartHealthDashboard90,
      chartHealthForecast90,
      chartHealthForecast12m,
      chartHealthForecast3y,
    };

    if (mode === 'live') {
      try {
        const apiPreferences: Record<string, string> = {
          lowCashBalanceThreshold: (parseFloat(lowCashThreshold) || defaults.lowCashBalanceThreshold).toString(),
          overdueInvoiceAlert: overdueAlert.toString(),
          varianceAlertThreshold: (parseFloat(varianceThreshold) || defaults.varianceAlertThreshold).toString(),
          emailNotifications: emailNotifications.toString(),
          feedNotifications: feedNotifications.toString(),
          defaultForecastView: defaultView,
          confidenceLevel,
          includeWeekends: includeWeekends.toString(),
          theme,
          defaultLandingPage,
        };
        await updateSelf(apiPreferences);
      } catch (error) {
        console.error('Failed to save preferences:', error);
        toast({
          title: 'Error',
          description: 'Failed to save preferences. Please try again.',
          variant: 'destructive',
        });
        setSaving(false);
        return;
      }
    }

    // Update context state
    updateUserPreferences(currentUser.id, preferencesPayload);

    // Apply theme visually
    setAppTheme(theme);
    // In demo mode, prevent next-themes from persisting to localStorage so
    // hard refresh always resets to the default light theme.
    if (mode === 'demo') {
      try { localStorage.removeItem('cash-forecaster-theme'); } catch (_) { /* ignore */ }
    }

    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  }, [
    lowCashThreshold, overdueAlert, varianceThreshold, emailNotifications,
    feedNotifications, defaultView, confidenceLevel, includeWeekends, theme,
    defaultLandingPage, mode, currentUser.id, defaults, updateUserPreferences, setAppTheme,
    healthyThreshold, cautionThreshold,
    chartHealthDashboard90, chartHealthForecast90, chartHealthForecast12m, chartHealthForecast3y,
  ]);

  // Build the list of pages this user can land on — mirrors sidebar permission logic.
  // Dashboard is only included if the user has at least one dashboard card permission.
  // Admin Dashboard is included if the user has admin card permissions.
  // This ensures the dropdown never shows a page that would be empty for this user.
  const availableLandingPages = React.useMemo(() => {
    const forecastTerm = getForecastTerm(true);
    const pages: { value: string; label: string }[] = [];

    // Dashboard — only if user has financial dashboard cards
    const hasDashboardCards = hasAnyPermission(
      'view_current_cash_mini_card', 'view_bank_accounts_card',
      'view_executive_cash_position_card',
      'view_30day_forecast_mini_card', 'view_90day_interactive_chart_card',
      'view_recent_transactions_card', 'view_overdue_invoices_mini_card',
      'view_upcoming_payments_mini_card', 'view_actions_card', 'view_variance_card',
      'view_uncategorized_items_card',
      'view_upcoming_recurring_transactions_card', 'view_scenario_comparison_card',
    );
    if (hasDashboardCards) {
      pages.push({ value: 'dashboard', label: 'Dashboard' });
    }

    if (hasAnyPermission('view_forecasts')) {
      pages.push({ value: 'forecast-90', label: `${forecastTerm}s — 90-Day View` });
      pages.push({ value: 'forecast-12m', label: `${forecastTerm}s — 12-Month View` });
      pages.push({ value: 'forecast-3y', label: `${forecastTerm}s — 3-Year View` });
    }
    if (hasAnyPermission('view_transactions')) {
      pages.push({ value: 'transactions', label: 'Transactions' });
      if (hasAnyPermission('manage_recurring_transactions')) {
        pages.push({ value: 'recurring-transactions', label: 'Recurring Transactions' });
      }
    }
    if (hasAnyPermission('view_scenarios')) {
      pages.push({ value: 'scenarios', label: getScenarioTerm() });
    }
    if (hasAnyPermission('view_variance_analysis_screen')) {
      pages.push({ value: 'variance', label: `${getVarianceTerm()} Analysis` });
    }
    if (hasAnyPermission('view_operational_insights', 'view_strategic_insights')) {
      pages.push({ value: 'recommendations', label: 'AI Recommendations' });
    }
    if (hasAnyPermission('view_reports')) {
      pages.push({ value: 'reports', label: 'Reports' });
    }
    return pages;
  }, [hasAnyPermission]);

  // Determine tab visibility based on permissions
  
  // Display Precision tab: requires BOTH allowUserOverride AND permission to view at least one dashboard card with monetary values
  const showDisplayPrecisionTab = canOverridePrecision && hasAnyPermission(
    'view_current_cash_mini_card',
    'view_bank_accounts_card',
    'view_executive_cash_position_card',
    'view_group_cash_position_card',
    'view_current_cash_mini_card',
    'view_30day_forecast_mini_card',
    'view_90day_interactive_chart_card',
    'view_scenario_comparison_card',
    'view_recent_transactions_card',
    'view_upcoming_recurring_transactions_card',
    'view_overdue_invoices_mini_card',
    'view_upcoming_payments_mini_card',
    'view_upcoming_payments_mini_card',
    'view_actions_card',
    'view_variance_card',
  );
  
  const showForecastAlertsTab = hasAnyPermission(
    'view_90day_interactive_chart_card', // Dashboard 90-day chart
    'view_forecasts', // Forecast timeline
  );

  const showTransactionAlertsTab = hasAnyPermission(
    'view_transactions',
    'edit_transaction_categories',
    'manage_recurring_transactions',
    'complete_actions',
  );

  const showVarianceAlertsTab = hasAnyPermission(
    'view_variance_analysis_screen',
    'view_variance_by_category',
  );

  const showForecastingTab = hasAnyPermission(
    'view_forecasts',
    'view_3year_forecast_screen',
  );

  return (
    <div className="space-y-6 p-6">
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving}>
          {saving ? (
            <>Saving...</>
          ) : saved ? (
            <>
              <CheckCircle2 className="h-4 w-4 mr-2" />
              Saved!
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Save Changes
            </>
          )}
        </Button>
      </div>

      <Tabs defaultValue={showDisplayPrecisionTab ? "display-precision" : showForecastAlertsTab ? "forecast-alerts" : showTransactionAlertsTab ? "transaction-alerts" : showVarianceAlertsTab ? "variance-alerts" : "notifications"} className="space-y-6">
        <TabsList>
          {showDisplayPrecisionTab && (
            <TabsTrigger value="display-precision">
              <User className="h-4 w-4 mr-2" />
              Display Precision
            </TabsTrigger>
          )}
          {showForecastAlertsTab && (
            <TabsTrigger value="forecast-alerts">
              <AlertTriangle className="h-4 w-4 mr-2" />
              Forecast Alerts
            </TabsTrigger>
          )}
          {showTransactionAlertsTab && (
            <TabsTrigger value="transaction-alerts">
              <Bell className="h-4 w-4 mr-2" />
              Transaction Alerts
            </TabsTrigger>
          )}
          {showVarianceAlertsTab && (
            <TabsTrigger value="variance-alerts">
              <AlertCircle className="h-4 w-4 mr-2" />
              Variance Alerts
            </TabsTrigger>
          )}
          <TabsTrigger value="notifications">
            <Bell className="h-4 w-4 mr-2" />
            Notifications
          </TabsTrigger>
          {showForecastingTab && (
            <TabsTrigger value="forecasting">
              <TrendingUp className="h-4 w-4 mr-2" />
              Forecasting
            </TabsTrigger>
          )}
          <TabsTrigger value="display">
            <Palette className="h-4 w-4 mr-2" />
            Display
          </TabsTrigger>
        </TabsList>

        {/* Display Precision Tab */}
        {showDisplayPrecisionTab && (
          <TabsContent value="display-precision" className="space-y-6">
            <Card>
              <CardContent className="space-y-6 pt-6">
                {canOverridePrecision ? (
                <>
                  <div className="space-y-4">
                    <div>
                      <Label className="text-base">Display Precision</Label>
                      <p className="text-sm text-muted-foreground mt-1">
                        Choose how financial amounts are displayed throughout the application
                      </p>
                    </div>

                    <RadioGroup
                      value={userPrecision || currentRole?.displayPrecision || 'full'}
                      onValueChange={(value) => handlePrecisionChange(value as DisplayPrecision)}
                    >
                      <div className="space-y-3">
                        <div className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-accent/50 cursor-pointer">
                          <RadioGroupItem value="full" id="precision-full" />
                          <div className="flex-1">
                            <Label htmlFor="precision-full" className="cursor-pointer font-medium">
                              Full Precision
                            </Label>
                            <p className="text-sm text-muted-foreground">
                              Show exact amounts with pence (e.g., £1,234,567.89)
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-accent/50 cursor-pointer">
                          <RadioGroupItem value="rounded" id="precision-rounded" />
                          <div className="flex-1">
                            <Label htmlFor="precision-rounded" className="cursor-pointer font-medium">
                              Rounded
                            </Label>
                            <p className="text-sm text-muted-foreground">
                              Round to nearest thousand (e.g., £1,235,000)
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-accent/50 cursor-pointer">
                          <RadioGroupItem value="abbreviated" id="precision-abbreviated" />
                          <div className="flex-1">
                            <Label htmlFor="precision-abbreviated" className="cursor-pointer font-medium">
                              Abbreviated
                            </Label>
                            <p className="text-sm text-muted-foreground">
                              Use K/M notation (e.g., £1.2M or £234.5K)
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-accent/50 cursor-pointer">
                          <RadioGroupItem value="millions" id="precision-millions" />
                          <div className="flex-1">
                            <Label htmlFor="precision-millions" className="cursor-pointer font-medium">
                              Millions
                            </Label>
                            <p className="text-sm text-muted-foreground">
                              Show in millions only (e.g., £1.23M)
                            </p>
                          </div>
                        </div>
                      </div>
                    </RadioGroup>

                    {userPrecision && userPrecision !== currentRole?.displayPrecision && (
                      <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="flex items-center gap-2">
                          <AlertCircle className="h-4 w-4 text-blue-600" />
                          <span className="text-sm text-blue-900">
                            Using custom precision (role default: {currentRole?.displayPrecision})
                          </span>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleResetPrecision}
                        >
                          Reset to Default
                        </Button>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="p-6 text-center text-muted-foreground">
                  <p>No user preferences available for your role.</p>
                  <p className="text-sm mt-2">
                    Your display precision is set by your role and cannot be changed.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        )}

        {/* Forecast Alerts Tab */}
        {showForecastAlertsTab && (
          <TabsContent value="forecast-alerts" className="space-y-6">
            <Card>
              <CardContent className="space-y-6 pt-6">
                <div className="space-y-2">
                  <Label>Low Cash Balance Threshold</Label>
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">£</span>
                    <Input
                      type="text"
                      inputMode="decimal"
                      value={lowCashThreshold}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || /^\d*\.?\d*$/.test(value)) {
                          setLowCashThreshold(value);
                        }
                      }}
                      className="max-w-[200px]"
                    />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Alert when cash falls below this threshold
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    This threshold drives alerts and notifications independently of chart display settings below.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Chart Health Zones */}
            <Card>
              <CardContent className="space-y-6 pt-6">
                <div>
                  <Label className="text-base font-semibold">Chart Health Zones</Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    Set the cash balance boundaries that define Healthy, Caution and Critical zones on your forecast charts.
                    Turning zones off on a chart does not affect your alert threshold above.
                  </p>
                </div>

                {/* Boundary values */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="healthy-threshold">Healthy above</Label>
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground">£</span>
                      <Input
                        id="healthy-threshold"
                        type="text"
                        inputMode="decimal"
                        value={healthyThreshold}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v === '' || /^\d*\.?\d*$/.test(v)) setHealthyThreshold(v);
                        }}
                        className="max-w-[160px]"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">Balance above this = Healthy (green)</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="caution-threshold">Caution above</Label>
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground">£</span>
                      <Input
                        id="caution-threshold"
                        type="text"
                        inputMode="decimal"
                        value={cautionThreshold}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v === '' || /^\d*\.?\d*$/.test(v)) setCautionThreshold(v);
                        }}
                        className="max-w-[160px]"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">Balance above this = Caution (amber), below = Critical (red)</p>
                  </div>
                </div>

                {/* Per-chart display toggles */}
                <div className="space-y-3">
                  <Label className="text-sm font-medium">Display per chart</Label>
                  <p className="text-xs text-muted-foreground -mt-1">
                    Choose how health zones appear on each chart. <span className="font-medium">Off</span> = clean chart, <span className="font-medium">Line only</span> = dashed threshold line, <span className="font-medium">Full bands</span> = coloured zones + line.
                  </p>
                  {(
                    [
                      { key: 'chartHealthDashboard90' as const, label: 'Dashboard — 90-Day card', value: chartHealthDashboard90, setter: setChartHealthDashboard90 },
                      { key: 'chartHealthForecast90' as const, label: 'Forecast — 90-Day view', value: chartHealthForecast90, setter: setChartHealthForecast90 },
                      { key: 'chartHealthForecast12m' as const, label: 'Forecast — 12-Month view', value: chartHealthForecast12m, setter: setChartHealthForecast12m },
                      { key: 'chartHealthForecast3y' as const, label: 'Forecast — 3-Year view', value: chartHealthForecast3y, setter: setChartHealthForecast3y },
                    ] as const
                  ).map(({ label, value, setter }) => (
                    <div key={label} className="flex items-center justify-between gap-4 py-2 border-b last:border-0">
                      <span className="text-sm">{label}</span>
                      <Select value={value} onValueChange={(v) => setter(v as ChartHealthDisplay)}>
                        <SelectTrigger className="w-[160px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="off">Off</SelectItem>
                          <SelectItem value="line">Line only</SelectItem>
                          <SelectItem value="bands">Full bands</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Transaction Alerts Tab */}
        {showTransactionAlertsTab && (
          <TabsContent value="transaction-alerts" className="space-y-6">
            <Card>
              <CardContent className="space-y-6 pt-6">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Overdue Invoice Alert</Label>
                    <p className="text-sm text-muted-foreground">
                      Alert when invoices become overdue
                    </p>
                  </div>
                  <Switch checked={overdueAlert} onCheckedChange={setOverdueAlert} />
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Variance Alerts Tab */}
        {showVarianceAlertsTab && (
          <TabsContent value="variance-alerts" className="space-y-6">
            <Card>
              <CardContent className="space-y-6 pt-6">
                <div className="space-y-2">
                  <Label>{getVarianceTerm(true)} Alert Threshold</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="text"
                      inputMode="decimal"
                      value={varianceThreshold}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || /^\d*\.?\d*$/.test(value)) {
                          setVarianceThreshold(value);
                        }
                      }}
                      className="max-w-[200px]"
                    />
                    <span className="text-muted-foreground">%</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Alert when forecast variance exceeds this threshold
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Notifications Tab */}
        <TabsContent value="notifications" className="space-y-4">
          <NotificationsTab />
        </TabsContent>

        {/* Forecasting Tab */}
        {showForecastingTab && (
          <TabsContent value="forecasting" className="space-y-6">
            <Card>
              <CardContent className="space-y-6 pt-6">
                <div className="space-y-2">
                  <Label>Default 90-Day View</Label>
                  <Select value={defaultView} onValueChange={setDefaultView}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="7-day">7 Days</SelectItem>
                      <SelectItem value="30-day">30 Days</SelectItem>
                      <SelectItem value="90-day">90 Days</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-sm text-muted-foreground">
                    Which period is pre-selected when you open the 90-Day forecast
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Confidence Level</Label>
                  <Select value={confidenceLevel} onValueChange={setConfidenceLevel}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low (70%)</SelectItem>
                      <SelectItem value="medium">Medium (90%)</SelectItem>
                      <SelectItem value="high">High (95%)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-sm text-muted-foreground">
                    Higher confidence means more conservative forecast accuracy
                  </p>
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Include Weekends</Label>
                    <p className="text-sm text-muted-foreground">
                      Show weekend days in daily forecasts
                    </p>
                  </div>
                  <Switch
                    checked={includeWeekends}
                    onCheckedChange={setIncludeWeekends}
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Display Tab */}
        <TabsContent value="display" className="space-y-6">

          {/* Avatar */}
          <Card>
            <CardContent className="pt-6">
              <div className="space-y-3">
                <Label>Profile Picture</Label>
                <div className="flex items-center gap-5">
                  <UserAvatar
                    name={currentUser.name}
                    seed={currentUser.id}
                    avatarUrl={avatarUrl}
                    size="lg"
                  />
                  <div className="space-y-1">
                    <p className="text-sm font-medium">{currentUser.name}</p>
                    <p className="text-xs text-muted-foreground">
                      Your profile picture is managed through your Evo Workspace profile and syncs automatically.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="space-y-6 pt-6">
              <div className="space-y-2">
                <Label>Theme</Label>
                <Select 
                  value={theme} 
                  onValueChange={(value) => {
                    setTheme(value);
                    setAppTheme(value);
                    // Write to context immediately so ThemeSync can read the
                    // correct value if the persona is switched before Save.
                    updateUserPreferences(currentUser.id, { theme: value as 'light' | 'dark' });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="light">Light</SelectItem>
                    <SelectItem value="dark">Dark</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Default Landing Page</Label>
                <Select value={defaultLandingPage} onValueChange={setDefaultLandingPage}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {availableLandingPages.map((page) => (
                      <SelectItem key={page.value} value={page.value}>
                        {page.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-sm text-muted-foreground">
                  The page you'll be taken to each time you open the app
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
